import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip as MuiTooltip,
  Stack,
  useTheme,
  Divider
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from "recharts";
import {
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon
} from "@mui/icons-material";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ExpenditureReport = () => {
  const theme = useTheme();
  
  // State for month/year selection - like PayrollRegister
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [monthName, setMonthName] = useState("");
  
  // State for data
  const [expenditureData, setExpenditureData] = useState(null);
  const [detailedData, setDetailedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI State
  const [showDetailed, setShowDetailed] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

// Replace the months array with:
const months = [
  { value: 0, label: "All Months" }, // Add this for full year
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" }
];

  // Update monthName whenever selectedMonth changes - like PayrollRegister
  useEffect(() => {
    const month = months.find(m => m.value === selectedMonth);
    setMonthName(month?.label || "");
  }, [selectedMonth]);

  // Generate years from 2020 to current year + 1
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= currentYear + 1; year++) {
      years.push(year);
    }
    setAvailableYears(years);
  }, []);

  // Fetch data when month or year changes - like PayrollRegister
  useEffect(() => {
    if (monthName) {
      fetchExpenditureData();
    }
  }, [selectedMonth, selectedYear, monthName]);

  const fetchExpenditureData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required. Please log in again.");
        setLoading(false);
        return;
      }
  
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        setError("Invalid authentication token");
        setLoading(false);
        return;
      }
  
      const payload = JSON.parse(atob(tokenParts[1]));
      const company_id = payload.company_id;
  
      if (!company_id) {
        setError("Company ID not found in token");
        setLoading(false);
        return;
      }
  
   
  
      const response = await axios.get(
        `${API_URL}/api/expenditure/consolidated-by-gender`,
        {
          params: {
            company_id,
            // Only send month if not "All Months" (value 0)
            ...(selectedMonth !== 0 && { month: selectedMonth }),
            year: selectedYear
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
  
      if (response.data.success) {
        if (response.data.data) {
          setExpenditureData(response.data.data);
          setError(null);
        } else {
          setExpenditureData(null);
          const message = selectedMonth === 0 
            ? `No expenditure data found for year ${selectedYear}`
            : response.data.message || `No expenditure data found for ${monthName} ${selectedYear}`;
          setError(message);
        }
      } else {
        setError(response.data.message || "Failed to fetch expenditure data");
        setExpenditureData(null);
      }
    } catch (err) {
      //console.error("Error fetching expenditure data:", err);
      
      if (err.response?.status === 404) {
        const message = selectedMonth === 0
          ? `No expenditure data found for year ${selectedYear}`
          : `No expenditure data found for ${monthName} ${selectedYear}`;
        setError(message);
      } else if (err.response) {
        setError(err.response.data.message || `Server error: ${err.response.status}`);
      } else if (err.request) {
        setError("No response from server. Please check your connection.");
      } else {
        setError("Error setting up request: " + err.message);
      }
      setExpenditureData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, monthName]);

  const fetchDetailedData = useCallback(async () => {
    if (!showDetailed || !expenditureData) return;
    
    try {
      const token = localStorage.getItem('token');
      const tokenParts = token.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      const company_id = payload.company_id;
  
      const response = await axios.get(
        `${API_URL}/api/expenditure/detailed-by-gender`,
        {
          params: { 
            company_id,
            // Only send month if not "All Months"
            ...(selectedMonth !== 0 && { month: selectedMonth }),
            year: selectedYear 
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
  
      if (response.data.success) {
        setDetailedData(response.data.data || []);
      }
    } catch (err) {
      //console.error("Error fetching detailed data:", err);
      setDetailedData([]);
    }
  }, [showDetailed, expenditureData, selectedMonth, selectedYear]);

  useEffect(() => {
    if (showDetailed && expenditureData) {
      fetchDetailedData();
    }
  }, [showDetailed, fetchDetailedData, expenditureData]);

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleRefresh = () => {
    fetchExpenditureData();
    if (showDetailed) {
      fetchDetailedData();
    }
  };

  const handleLoadData = () => {
    fetchExpenditureData();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    const num = Number(amount);
    if (isNaN(num)) return '₹0';
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    const value = Number(num);
    if (isNaN(value)) return '0';
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const preparePieData = () => {
    if (!expenditureData) return [];
    
    return [
      { 
        name: 'Male', 
        value: Number(expenditureData.male?.total_expense) || 0,
        count: Number(expenditureData.male?.employeeCount) || 0
      },
      { 
        name: 'Female', 
        value: Number(expenditureData.female?.total_expense) || 0,
        count: Number(expenditureData.female?.employeeCount) || 0
      }
    ];
  };

  const prepareBarData = () => {
    if (!expenditureData) return [];
    
    const maleEarnings = expenditureData.male?.earnings || {};
    const femaleEarnings = expenditureData.female?.earnings || {};
    const maleEmployerCosts = expenditureData.male?.employerCosts || {};
    const femaleEmployerCosts = expenditureData.female?.employerCosts || {};

    return [
      {
        category: 'Base Salary',
        Male: Number(maleEarnings.base_salary) || 0,
        Female: Number(femaleEarnings.base_salary) || 0
      },
      {
        category: 'HRA',
        Male: Number(maleEarnings.hra) || 0,
        Female: Number(femaleEarnings.hra) || 0
      },
      {
        category: 'Special Allowance',
        Male: Number(maleEarnings.special_allowance) || 0,
        Female: Number(femaleEarnings.special_allowance) || 0
      },
      {
        category: 'Bonus',
        Male: (Number(maleEarnings.performance_bonus) || 0) + (Number(maleEarnings.joining_bonus) || 0),
        Female: (Number(femaleEarnings.performance_bonus) || 0) + (Number(femaleEarnings.joining_bonus) || 0)
      },
      {
        category: 'Variable Pay',
        Male: Number(maleEarnings.variable_salary) || 0,
        Female: Number(femaleEarnings.variable_salary) || 0
      },
      {
        category: 'Employer Costs',
        Male: Number(maleEmployerCosts.total_employer_cost) || 0,
        Female: Number(femaleEmployerCosts.total_employer_cost) || 0
      }
    ];
  };

  const getPeriodText = () => {
    if (selectedMonth === 0) {
      return `Full Year ${selectedYear}`;
    }
    return `${monthName} ${selectedYear}`;
  };
  const exportToPDF = () => {
    if (!expenditureData) return;
    
    const doc = new jsPDF();
    const maleEarnings = expenditureData.male?.earnings || {};
    const femaleEarnings = expenditureData.female?.earnings || {};
    const maleEmployerCosts = expenditureData.male?.employerCosts || {};
    const femaleEmployerCosts = expenditureData.female?.employerCosts || {};
    const grandTotal = expenditureData.grandTotal || {};
    
    // Title
    doc.setFontSize(18);
    doc.text("Gender-Wise Expenditure Report", 14, 22);
    doc.setFontSize(12);
    doc.text(`Period: ${getPeriodText()}`, 14, 32);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 40);
    
    // Summary Table
    const summaryData = [
      ["Category", "Male (₹)", "Female (₹)", "Total (₹)"],
      ["Number of Employees", 
       String(expenditureData.male?.employeeCount || 0),
       String(expenditureData.female?.employeeCount || 0),
       String(grandTotal.employeeCount || 0)],
      ["Base Salary",
       String(maleEarnings.base_salary || 0),
       String(femaleEarnings.base_salary || 0),
       String((maleEarnings.base_salary || 0) + (femaleEarnings.base_salary || 0))],
      ["HRA",
       String(maleEarnings.hra || 0),
       String(femaleEarnings.hra || 0),
       String((maleEarnings.hra || 0) + (femaleEarnings.hra || 0))],
      ["Special Allowance",
       String(maleEarnings.special_allowance || 0),
       String(femaleEarnings.special_allowance || 0),
       String((maleEarnings.special_allowance || 0) + (femaleEarnings.special_allowance || 0))],
      ["Variable Salary",
       String(maleEarnings.variable_salary || 0),
       String(femaleEarnings.variable_salary || 0),
       String((maleEarnings.variable_salary || 0) + (femaleEarnings.variable_salary || 0))],
      ["Performance Bonus",
       String(maleEarnings.performance_bonus || 0),
       String(femaleEarnings.performance_bonus || 0),
       String((maleEarnings.performance_bonus || 0) + (femaleEarnings.performance_bonus || 0))],
      ["Joining Bonus",
       String(maleEarnings.joining_bonus || 0),
       String(femaleEarnings.joining_bonus || 0),
       String((maleEarnings.joining_bonus || 0) + (femaleEarnings.joining_bonus || 0))],
      ["Total Earnings",
       String(maleEarnings.total_earnings || 0),
       String(femaleEarnings.total_earnings || 0),
       String(grandTotal.total_earnings || 0)],
      ["Employer PF",
       String(maleEmployerCosts.employer_pf || 0),
       String(femaleEmployerCosts.employer_pf || 0),
       String((maleEmployerCosts.employer_pf || 0) + (femaleEmployerCosts.employer_pf || 0))],
      ["Gratuity",
       String(maleEmployerCosts.gratuity || 0),
       String(femaleEmployerCosts.gratuity || 0),
       String((maleEmployerCosts.gratuity || 0) + (femaleEmployerCosts.gratuity || 0))],
      ["Medical Insurance",
       String(maleEmployerCosts.medical_insurance || 0),
       String(femaleEmployerCosts.medical_insurance || 0),
       String((maleEmployerCosts.medical_insurance || 0) + (femaleEmployerCosts.medical_insurance || 0))],
      ["Total Employer Cost",
       String(maleEmployerCosts.total_employer_cost || 0),
       String(femaleEmployerCosts.total_employer_cost || 0),
       String(grandTotal.total_employer_cost || 0)],
      ["GRAND TOTAL",
       String(expenditureData.male?.total_expense || 0),
       String(expenditureData.female?.total_expense || 0),
       String(grandTotal.total_expense || 0)]
    ];

    autoTable(doc, {
      startY: 50,
      head: [summaryData[0]],
      body: summaryData.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [25, 118, 210], textColor: 255 },
      styles: { 
        fontSize: 9, 
        cellPadding: 2,
        halign: 'right'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50, halign: 'left' }
      }
    });

    // Add detailed data if available
    if (detailedData.length > 0) {
      doc.addPage();
      doc.setFontSize(16);
      doc.text("Detailed Employee Expenditure", 14, 22);
      
      const detailedTableData = [
        ["Emp ID", "Name", "Gender", "Dept", "Base Salary", "HRA", "Allowance", "Bonus", "Employer Cost", "Total"]
      ];

      detailedData.forEach(emp => {
        if (emp.payroll) {
          const earnings = (Number(emp.payroll.base_salary) || 0) +
                          (Number(emp.payroll.hra) || 0) +
                          (Number(emp.payroll.special_allowance) || 0) +
                          (Number(emp.payroll.performance_bonus) || 0) +
                          (Number(emp.payroll.joining_bonus) || 0) +
                          (Number(emp.payroll.variable_salary) || 0);
          
          const employerCost = (Number(emp.payroll.employer_pf) || 0) +
                              (Number(emp.payroll.gratuity) || 0) +
                              (Number(emp.payroll.medical_insurance) || 0);
          
          const total = earnings + employerCost;

          detailedTableData.push([
            emp.employee_id,
            emp.name,
            emp.gender,
            emp.department || 'N/A',
            String(emp.payroll.base_salary || 0),
            String(emp.payroll.hra || 0),
            String(emp.payroll.special_allowance || 0),
            String((Number(emp.payroll.performance_bonus) || 0) + (Number(emp.payroll.joining_bonus) || 0)),
            String(employerCost),
            String(total)
          ]);
        }
      });

      autoTable(doc, {
        startY: 30,
        head: [detailedTableData[0]],
        body: detailedTableData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [25, 118, 210], textColor: 255 },
        styles: { 
          fontSize: 8,
          halign: 'right'
        },
        columnStyles: {
          0: { cellWidth: 20, halign: 'left' },
          1: { cellWidth: 35, halign: 'left' },
          2: { cellWidth: 15, halign: 'left' },
          3: { cellWidth: 25, halign: 'left' },
          4: { cellWidth: 22, halign: 'right' },
          5: { cellWidth: 18, halign: 'right' },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 18, halign: 'right' },
          8: { cellWidth: 22, halign: 'right' },
          9: { cellWidth: 22, halign: 'right' }
        }
      });
    }

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    doc.save(`Expenditure_Report_${selectedMonth}_${selectedYear}.pdf`);
  };

  const exportToExcel = () => {
    if (!expenditureData) return;
    
    const wb = XLSX.utils.book_new();
    const maleEarnings = expenditureData.male?.earnings || {};
    const femaleEarnings = expenditureData.female?.earnings || {};
    const maleEmployerCosts = expenditureData.male?.employerCosts || {};
    const femaleEmployerCosts = expenditureData.female?.employerCosts || {};
    const grandTotal = expenditureData.grandTotal || {};
    
    // Summary Sheet
    const summarySheetData = [
      ["Gender-Wise Expenditure Report"],
      [`Period: ${getPeriodText()}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["Category", "Male (₹)", "Female (₹)", "Total (₹)"],
      ["Number of Employees", 
       expenditureData.male?.employeeCount || 0,
       expenditureData.female?.employeeCount || 0,
       grandTotal.employeeCount || 0],
      ["Base Salary",
       maleEarnings.base_salary || 0,
       femaleEarnings.base_salary || 0,
       (maleEarnings.base_salary || 0) + (femaleEarnings.base_salary || 0)],
      ["HRA",
       maleEarnings.hra || 0,
       femaleEarnings.hra || 0,
       (maleEarnings.hra || 0) + (femaleEarnings.hra || 0)],
      ["Special Allowance",
       maleEarnings.special_allowance || 0,
       femaleEarnings.special_allowance || 0,
       (maleEarnings.special_allowance || 0) + (femaleEarnings.special_allowance || 0)],
      ["Variable Salary",
       maleEarnings.variable_salary || 0,
       femaleEarnings.variable_salary || 0,
       (maleEarnings.variable_salary || 0) + (femaleEarnings.variable_salary || 0)],
      ["Performance Bonus",
       maleEarnings.performance_bonus || 0,
       femaleEarnings.performance_bonus || 0,
       (maleEarnings.performance_bonus || 0) + (femaleEarnings.performance_bonus || 0)],
      ["Joining Bonus",
       maleEarnings.joining_bonus || 0,
       femaleEarnings.joining_bonus || 0,
       (maleEarnings.joining_bonus || 0) + (femaleEarnings.joining_bonus || 0)],
      ["Total Earnings",
       maleEarnings.total_earnings || 0,
       femaleEarnings.total_earnings || 0,
       grandTotal.total_earnings || 0],
      ["Employer PF",
       maleEmployerCosts.employer_pf || 0,
       femaleEmployerCosts.employer_pf || 0,
       (maleEmployerCosts.employer_pf || 0) + (femaleEmployerCosts.employer_pf || 0)],
      ["Gratuity",
       maleEmployerCosts.gratuity || 0,
       femaleEmployerCosts.gratuity || 0,
       (maleEmployerCosts.gratuity || 0) + (femaleEmployerCosts.gratuity || 0)],
      ["Medical Insurance",
       maleEmployerCosts.medical_insurance || 0,
       femaleEmployerCosts.medical_insurance || 0,
       (maleEmployerCosts.medical_insurance || 0) + (femaleEmployerCosts.medical_insurance || 0)],
      ["Total Employer Cost",
       maleEmployerCosts.total_employer_cost || 0,
       femaleEmployerCosts.total_employer_cost || 0,
       grandTotal.total_employer_cost || 0],
      ["GRAND TOTAL",
       expenditureData.male?.total_expense || 0,
       expenditureData.female?.total_expense || 0,
       grandTotal.total_expense || 0]
    ];

    const ws = XLSX.utils.aoa_to_sheet(summarySheetData);
    ws['!cols'] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Summary");

    // Detailed Sheet
    if (detailedData.length > 0) {
      const detailedSheetData = [
        ["Employee ID", "Name", "Gender", "Department", "Base Salary", "HRA", "Special Allowance", 
         "Performance Bonus", "Joining Bonus", "Variable Salary", "Employer PF", "Gratuity", 
         "Medical Insurance", "Total Cost"]
      ];

      detailedData.forEach(emp => {
        if (emp.payroll) {
          const totalCost = 
            (Number(emp.payroll.base_salary) || 0) +
            (Number(emp.payroll.hra) || 0) +
            (Number(emp.payroll.special_allowance) || 0) +
            (Number(emp.payroll.performance_bonus) || 0) +
            (Number(emp.payroll.joining_bonus) || 0) +
            (Number(emp.payroll.variable_salary) || 0) +
            (Number(emp.payroll.employer_pf) || 0) +
            (Number(emp.payroll.gratuity) || 0) +
            (Number(emp.payroll.medical_insurance) || 0);

          detailedSheetData.push([
            emp.employee_id,
            emp.name,
            emp.gender,
            emp.department || 'N/A',
            emp.payroll.base_salary || 0,
            emp.payroll.hra || 0,
            emp.payroll.special_allowance || 0,
            emp.payroll.performance_bonus || 0,
            emp.payroll.joining_bonus || 0,
            emp.payroll.variable_salary || 0,
            emp.payroll.employer_pf || 0,
            emp.payroll.gratuity || 0,
            emp.payroll.medical_insurance || 0,
            totalCost
          ]);
        }
      });

      const wsDetailed = XLSX.utils.aoa_to_sheet(detailedSheetData);
      wsDetailed['!cols'] = [
        { wch: 12 }, { wch: 20 }, { wch: 8 }, { wch: 15 },
        { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
        { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
        { wch: 12 }, { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, wsDetailed, "Detailed");
    }

    XLSX.writeFile(wb, `Expenditure_Report_${selectedMonth}_${selectedYear}.xlsx`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section - Like PayrollRegister */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
  {/* Left side - Title */}
  <Box>
    <Typography variant="h4" fontWeight="bold">
      Gender-Wise Expenditure
    </Typography>
    <Typography variant="body2" color="textSecondary">
      Consolidated employee cost analysis
    </Typography>
  </Box>
  
  {/* Right side - Filters */}
  <Box display="flex" gap={2} alignItems="center">
    {/* Month Selector */}
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <InputLabel>Month</InputLabel>
      <Select
        value={selectedMonth}
        onChange={handleMonthChange}
        label="Month"
      >
        {months.map((month) => (
          <MenuItem key={month.value} value={month.value}>
            {month.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* Year Selector */}
    <FormControl size="small" sx={{ minWidth: 100 }}>
      <InputLabel>Year</InputLabel>
      <Select
        value={selectedYear}
        onChange={handleYearChange}
        label="Year"
      >
        {availableYears.map((year) => (
          <MenuItem key={year} value={year}>{year}</MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* Buttons */}
    <Box display="flex" gap={1}>
      <Button
        variant="contained"
        onClick={handleLoadData}
        disabled={loading}
        size="small"
      >
        {loading ? <CircularProgress size={24} /> : 'Load'}
      </Button>
     
    </Box>
  </Box>
</Box>

        <Divider sx={{ my: 3 }} />

        {/* Export Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={exportToExcel}
            disabled={!expenditureData || loading}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={exportToPDF}
            disabled={!expenditureData || loading}
          >
            PDF
          </Button>
        </Box>
      </Paper>

      {/* Error Alert - Like PayrollRegister */}
      {error && (
        <Alert 
          severity="info"
          sx={{ 
            mb: 3, 
            backgroundColor: '#e3f2fd', 
            color: '#0d47a1',
            '& .MuiAlert-icon': {
              color: '#0d47a1'
            }
          }}
          action={
            <Button color="primary" size="small" onClick={handleLoadData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Empty State - Like PayrollRegister */}
      {!expenditureData && !loading && (
        <Paper 
          sx={{ 
            p: 5, 
            textAlign: 'center', 
            borderRadius: 2,
            backgroundColor: '#f5f5f5',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <ReceiptIcon sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
          <Typography variant="h5" color="textSecondary" gutterBottom>
            No expenditure data found
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: 500 }}>
            {error ? error : `No expenditure data found for ${monthName} ${selectedYear}. Please select a different period or upload payroll data first.`}
          </Typography>
         
        </Paper>
      )}

      {/* Data Display */}
      {expenditureData && !error && (
        <>
        {/* Summary Cards - Like PayrollRegister */}
<Grid container spacing={3} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#e3f2fd',
      color: '#1565c0',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL EMPLOYEES
        </Typography>
        <Typography variant="h4" fontWeight="600" sx={{ mt: 1,fontSize:20 }}>
          {formatNumber(expenditureData.grandTotal?.employeeCount || 0)}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Chip 
            size="small" 
            label={`Male: ${formatNumber(expenditureData.male?.employeeCount || 0)}`}
            variant="outlined"
            sx={{ borderColor: '#1565c0', color: '#1565c0' }}
          />
          <Chip 
            size="small" 
            label={`Female: ${formatNumber(expenditureData.female?.employeeCount || 0)}`}
            variant="outlined"
            sx={{ borderColor: '#c2185b', color: '#c2185b' }}
          />
        </Box>
      </CardContent>
    </Card>
  </Grid>
  
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#e8f5e8',
      color: '#2e7d32',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          TOTAL EARNINGS
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(expenditureData.grandTotal?.earnings?.total_earnings || 0)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Employee salaries & bonuses
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#fff3e0',
      color: '#e65100',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          EMPLOYER COSTS
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(expenditureData.grandTotal?.employerCosts?.total_employer_cost || 0)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          PF, Gratuity, Insurance
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ 
      bgcolor: '#f3e5f5',
      color: '#6a1b9a',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }
    }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#546e7a', fontSize: '0.7rem' }}>
          GRAND TOTAL
        </Typography>
        <Typography variant="h6" fontWeight="600" sx={{ mt: 1 }}>
          {formatCurrency(expenditureData.grandTotal?.total_expense || 0)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Total expenditure
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Expenditure Distribution by Gender
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={preparePieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent, count }) => 
                        `${name}: ${(percent * 100).toFixed(1)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {preparePieData().map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={index === 0 ? theme.palette.primary.main : theme.palette.secondary.main} 
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Cost Breakdown by Category
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={prepareBarData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" angle={-45} textAnchor="end" height={60} />
                    <YAxis tickFormatter={(value) => `₹${(value/100000).toFixed(1)}L`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="Male" fill={theme.palette.primary.main} />
                    <Bar dataKey="Female" fill={theme.palette.secondary.main} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Main Table */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Consolidated Expenditure Details
            </Typography>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
              {getPeriodText()}
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Male (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Female (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Earnings Section */}
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      <Chip label="EMPLOYEE EARNINGS" size="small" color="primary" sx={{ mr: 1 }} />
                    </TableCell>
                  </TableRow>
                  
                  {[
                    { label: 'Base Salary', male: expenditureData.male?.earnings?.base_salary, female: expenditureData.female?.earnings?.base_salary },
                    { label: 'HRA', male: expenditureData.male?.earnings?.hra, female: expenditureData.female?.earnings?.hra },
                    { label: 'Special Allowance', male: expenditureData.male?.earnings?.special_allowance, female: expenditureData.female?.earnings?.special_allowance },
                    { label: 'Variable Salary', male: expenditureData.male?.earnings?.variable_salary, female: expenditureData.female?.earnings?.variable_salary },
                    { label: 'Performance Bonus', male: expenditureData.male?.earnings?.performance_bonus, female: expenditureData.female?.earnings?.performance_bonus },
                    { label: 'Joining Bonus', male: expenditureData.male?.earnings?.joining_bonus, female: expenditureData.female?.earnings?.joining_bonus }
                  ].map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{item.label}</TableCell>
                      <TableCell align="right">{formatCurrency(item.male)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.female)}</TableCell>
                      <TableCell align="right">{formatCurrency((item.male || 0) + (item.female || 0))}</TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Earnings</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.male?.earnings?.total_earnings)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.female?.earnings?.total_earnings)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.grandTotal?.total_earnings)}</TableCell>
                  </TableRow>

                  {/* Employer Costs Section */}
                  <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      <Chip label="EMPLOYER COSTS" size="small" sx={{ mr: 1, backgroundColor: '#ff9800', color: 'white' }} />
                    </TableCell>
                  </TableRow>

                  {[
                    { label: 'Employer PF', male: expenditureData.male?.employerCosts?.employer_pf, female: expenditureData.female?.employerCosts?.employer_pf },
                    { label: 'Gratuity', male: expenditureData.male?.employerCosts?.gratuity, female: expenditureData.female?.employerCosts?.gratuity },
                    { label: 'Medical Insurance', male: expenditureData.male?.employerCosts?.medical_insurance, female: expenditureData.female?.employerCosts?.medical_insurance }
                  ].map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{item.label}</TableCell>
                      <TableCell align="right">{formatCurrency(item.male)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.female)}</TableCell>
                      <TableCell align="right">{formatCurrency((item.male || 0) + (item.female || 0))}</TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Employer Cost</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.male?.employerCosts?.total_employer_cost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.female?.employerCosts?.total_employer_cost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expenditureData.grandTotal?.total_employer_cost)}</TableCell>
                  </TableRow>

                  {/* Grand Total */}
                  <TableRow sx={{ backgroundColor: '#f3e5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>GRAND TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                      {formatCurrency(expenditureData.male?.total_expense || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                      {formatCurrency(expenditureData.female?.total_expense || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                      {formatCurrency(expenditureData.grandTotal?.total_expense || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Toggle Detailed View Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowDetailed(!showDetailed)}
                disabled={!expenditureData}
              >
                {showDetailed ? 'Hide' : 'Show'} Detailed Breakdown
              </Button>
            </Box>

            {/* Detailed Data Table */}
            {showDetailed && detailedData.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                  Detailed Employee Expenditure
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                        <TableCell>Emp ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Gender</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Base Salary</TableCell>
                        <TableCell align="right">HRA</TableCell>
                        <TableCell align="right">Allowance</TableCell>
                        <TableCell align="right">Bonus</TableCell>
                        <TableCell align="right">Employer Cost</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailedData.map((emp, index) => {
                        if (!emp.payroll) return null;
                        const total = (Number(emp.payroll.base_salary) || 0) +
                                    (Number(emp.payroll.hra) || 0) +
                                    (Number(emp.payroll.special_allowance) || 0) +
                                    (Number(emp.payroll.performance_bonus) || 0) +
                                    (Number(emp.payroll.joining_bonus) || 0) +
                                    (Number(emp.payroll.variable_salary) || 0) +
                                    (Number(emp.payroll.employer_pf) || 0) +
                                    (Number(emp.payroll.gratuity) || 0) +
                                    (Number(emp.payroll.medical_insurance) || 0);
                        
                        const employerCost = (Number(emp.payroll.employer_pf) || 0) +
                                            (Number(emp.payroll.gratuity) || 0) +
                                            (Number(emp.payroll.medical_insurance) || 0);
                        
                        return (
                          <TableRow key={index} hover>
                            <TableCell>{emp.employee_id}</TableCell>
                            <TableCell>{emp.name}</TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={emp.gender}
                                color={emp.gender?.toLowerCase() === 'male' ? 'primary' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{emp.department}</TableCell>
                            <TableCell align="right">{formatCurrency(emp.payroll.base_salary)}</TableCell>
                            <TableCell align="right">{formatCurrency(emp.payroll.hra)}</TableCell>
                            <TableCell align="right">{formatCurrency(emp.payroll.special_allowance)}</TableCell>
                            <TableCell align="right">{formatCurrency((Number(emp.payroll.performance_bonus) || 0) + (Number(emp.payroll.joining_bonus) || 0))}</TableCell>
                            <TableCell align="right">{formatCurrency(employerCost)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(total)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {showDetailed && detailedData.length === 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                No detailed data available for the selected period
              </Alert>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default ExpenditureReport;